<template>
    <div class="home">
        <div class="camera-container">
            <h2>设备识别</h2>
            <div class="camera-info" v-if="availableCameras.length > 1">
    当前摄像头: {{ currentCameraName }}
  </div>
            <!-- 摄像头视频流 -->
            <div v-if="!capturedImage" class="video-container">
                <video 
                    ref="videoRef" 
                    autoplay 
                    playsinline 
                    class="video-stream"
                ></video>
                <div class="camera-controls">
                    <el-button 
                        v-if="availableCameras.length > 1"
                        type="primary" 
                        circle 
                        size="small" 
                        @click="switchCamera"
                        class="switch-camera-btn"
                    >
                        <el-icon size="16"><Refresh /></el-icon>
                    </el-button>
                    
                    <el-button 
                        type="primary" 
                        circle 
                        size="large" 
                        @click="capturePhoto"
                        class="capture-btn"
                    >
                        <el-icon size="24"><Camera /></el-icon>
                    </el-button>
                </div>
            </div>
            
            <!-- 拍摄的照片预览 -->
            <div v-else class="preview-container">
                <img :src="capturedImage" alt="Captured" class="captured-image" />
                <div class="preview-controls">
                    <el-button @click="retakePhoto">重拍</el-button>
                    <el-button type="primary" @click="uploadPhotoAsBase64" :loading="uploading" v-if="uploadMethod === 'base64'">
                        {{ uploading ? '上传中...' : 'Base64识别' }}
                    </el-button>
                    <el-button type="success" @click="uploadPhotoAsImage" :loading="uploading" v-else>
                        {{ uploading ? '上传中...' : '图片识别' }}
                    </el-button>
                    <el-select v-model="uploadMethod" placeholder="选择上传方式" size="small">
                        <el-option label="Base64识别" value="base64"></el-option>
                        <el-option label="图片识别" value="image"></el-option>
                    </el-select>
                </div>
            </div>
            
            <!-- 识别结果列表 -->
            <div v-if="recognitionResults.length > 0" class="results-container">
                <h3>识别记录</h3>
                <el-table :data="recognitionResults" :style="{ width: '100%' }" stripe>
                    <el-table-column prop="timestamp" label="时间" width="180">
                        <template #default="scope">
                            {{ formatTime(scope.row.timestamp) }}
                        </template>
                    </el-table-column>
                    <el-table-column label="照片" width="120">
                        <template #default="scope">
                            <img :src="scope.row.image" class="result-image" />
                        </template>
                    </el-table-column>
                    <el-table-column prop="data" label="识别结果">
                        <template #default="scope">
                            <div v-if="scope.row.result">
                               
                                    {{ scope.row.result }}
                               
                            </div>
                            <div v-else class="no-result">
                                无识别结果
                            </div>
                        </template>
                    </el-table-column>
                </el-table>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount, onUnmounted, computed } from 'vue'
import { Camera, Refresh } from '@element-plus/icons-vue'
import { ElButton, ElIcon, ElTable, ElTableColumn, ElMessage, ElSelect, ElOption } from 'element-plus'
import getDatas from '@/network/index'

// 视频引用
const videoRef = ref<HTMLVideoElement | null>(null)
// 拍摄的照片
const capturedImage = ref<string | null>(null)
// 媒体流
const mediaStream = ref<MediaStream | null>(null)
// 上传状态
const uploading = ref(false)
// 识别结果列表
const recognitionResults = ref<Array<{
    id: number,
    image: string,
    result: any,
    timestamp: Date
}>>([])
// 摄像头状态
const cameraStatus = ref<'idle' | 'loading' | 'success' | 'error'>('idle')
// 错误信息
const errorMessage = ref('')
// 可用摄像头列表
const availableCameras = ref<MediaDeviceInfo[]>([])
// 当前使用的摄像头索引
const currentCameraIndex = ref(0)
// 上传方法 ('base64' 或 'image')
const uploadMethod = ref<'base64' | 'image'>('base64')

// 图片压缩配置
const compressConfig = {
    maxWidth: 1280,      // 最大宽度
    maxHeight: 960,      // 最大高度
    quality: 0.7,        // 压缩质量 (0-1)
    maxSizeKB: 500       // 目标最大文件大小 (KB)
}

/**
 * 获取base64字符串的大小（字节）
 */
const getBase64Size = (base64Str: string): number => {
    // 移除data:image/xxx;base64,前缀
    const base64 = base64Str.split(',')[1] || base64Str
    // base64编码后大小约为原始大小的4/3
    return Math.round((base64.length * 3) / 4)
}

/**
 * 压缩图片
 * @param base64Str 原始base64图片字符串
 * @param config 压缩配置
 * @returns 压缩后的base64图片字符串
 */
const compressImage = (base64Str: string, config = compressConfig): Promise<string> => {
    return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
            try {
                // 计算压缩后的尺寸
                let { width, height } = img
                const { maxWidth, maxHeight, quality } = config
                
                // 按比例缩放
                if (width > maxWidth || height > maxHeight) {
                    const ratio = Math.min(maxWidth / width, maxHeight / height)
                    width = Math.round(width * ratio)
                    height = Math.round(height * ratio)
                }
                
                // 创建canvas进行压缩
                const canvas = document.createElement('canvas')
                canvas.width = width
                canvas.height = height
                
                const ctx = canvas.getContext('2d')
                if (!ctx) {
                    reject(new Error('无法创建canvas上下文'))
                    return
                }
                
                // 绘制图片
                ctx.drawImage(img, 0, 0, width, height)
                
                // 转换为base64，使用指定质量
                let compressedBase64 = canvas.toDataURL('image/jpeg', quality)
                
                // 如果还是太大，继续降低质量
                let currentQuality = quality
                const maxSizeBytes = config.maxSizeKB * 1024
                
                while (getBase64Size(compressedBase64) > maxSizeBytes && currentQuality > 0.1) {
                    currentQuality -= 0.1
                    compressedBase64 = canvas.toDataURL('image/jpeg', currentQuality)
                }
                
                console.log(`图片压缩完成: 原尺寸 ${img.width}x${img.height} -> ${width}x${height}, 质量: ${currentQuality.toFixed(1)}, 大小: ${(getBase64Size(compressedBase64) / 1024).toFixed(1)}KB`)
                
                resolve(compressedBase64)
            } catch (err) {
                reject(err)
            }
        }
        img.onerror = () => reject(new Error('图片加载失败'))
        img.src = base64Str
    })
}

// 获取可用摄像头设备
const getAvailableCameras = async () => {
    try {
        if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
            const devices = await navigator.mediaDevices.enumerateDevices();
            availableCameras.value = devices.filter(device => device.kind === 'videoinput');
            console.log('可用摄像头:', availableCameras.value);
        }
    } catch (err) {
        console.error('获取摄像头设备列表失败:', err);
    }
}

// 判断是否为前置摄像头
const isFrontCamera = (device: MediaDeviceInfo): boolean => {
  const frontCameraKeywords = ['front', '前置', 'user', 'selfie', 'face'];
  const label = (device.label || '').toLowerCase();
  return frontCameraKeywords.some(keyword => label.includes(keyword));
};

// 判断是否为后置摄像头
const isBackCamera = (device: MediaDeviceInfo): boolean => {
  const backCameraKeywords = ['back', 'rear', '后置', 'environment'];
  const label = (device.label || '').toLowerCase();
  return backCameraKeywords.some(keyword => label.includes(keyword));
};

// 获取前后摄像头列表
const frontCameras = computed(() => {
  return availableCameras.value.filter(device => isFrontCamera(device));
});

const backCameras = computed(() => {
  return availableCameras.value.filter(device => isBackCamera(device));
});

// 初始化摄像头
const initCamera = async () => {
    try {
        cameraStatus.value = 'loading'
        errorMessage.value = ''
        
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            throw new Error('您的浏览器不支持摄像头功能，请更换现代浏览器')
        }
        
        // 获取可用摄像头列表
        await getAvailableCameras();
        
        // 先尝试使用指定摄像头
        let stream: MediaStream;
        try {
            const constraints: MediaStreamConstraints = { 
                video: {}, 
                audio: false 
            };
            
            // 如果有可用的摄像头列表，使用指定的摄像头
            if (availableCameras.value.length > 0 && availableCameras.value[currentCameraIndex.value]) {
                constraints.video = {
                    deviceId: { exact: availableCameras.value[currentCameraIndex.value].deviceId },
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                };
            } else {
                // 否则使用默认约束
                constraints.video = {
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                };
            }
            
            stream = await navigator.mediaDevices.getUserMedia(constraints);
        } catch (err) {
            console.warn('无法访问指定摄像头，尝试使用默认摄像头:', err)
            // 如果指定摄像头不可用，尝试默认摄像头
            try {
                stream = await navigator.mediaDevices.getUserMedia({ 
                    video: { 
                        width: { ideal: 1280 },
                        height: { ideal: 720 }
                    }, 
                    audio: false 
                })
            } catch (fallbackErr) {
                console.warn('无法访问默认摄像头:', fallbackErr)
                // 如果还是失败，尝试最基本的配置
                stream = await navigator.mediaDevices.getUserMedia({ 
                    video: true, 
                    audio: false 
                })
            }
        }
        
        mediaStream.value = stream
        cameraStatus.value = 'success'
        
        if (videoRef.value) {
            videoRef.value.srcObject = stream
        }
        
    } catch (err) {
        cameraStatus.value = 'error'
        console.error('获取摄像头权限失败:', err)
        
        // 根据错误类型提供不同的提示信息
        if (err instanceof DOMException) {
            switch (err.name) {
                case 'NotFoundError':
                case 'OverconstrainedError':
                    errorMessage.value = '未找到可用的摄像头设备，请检查设备是否连接正常'
                    break
                case 'NotAllowedError':
                case 'SecurityError':
                    errorMessage.value = '摄像头访问被拒绝，请允许浏览器访问摄像头权限'
                    break
                case 'NotReadableError':
                    errorMessage.value = '摄像头正在被其他应用程序占用，请关闭其他使用摄像头的程序'
                    break
                case 'AbortError':
                    errorMessage.value = '摄像头访问被中断'
                    break
                default:
                    errorMessage.value = `摄像头访问失败: ${err.message}`
            }
        } else if (err instanceof Error) {
            errorMessage.value = err.message
        } else {
            errorMessage.value = '未知错误导致摄像头访问失败'
        }
        
        ElMessage.error(errorMessage.value)
    }
}

// 翻转摄像头
const switchCamera = async () => {
    // 如果只有一个摄像头，则不切换
    if (availableCameras.value.length <= 1) return;
    
    // 切换到下一个摄像头
    currentCameraIndex.value = (currentCameraIndex.value + 1) % availableCameras.value.length;
    
    // 重新初始化摄像头
    cleanup()
    await initCamera()
}

// 添加当前摄像头名称的响应式变量
const currentCameraName = computed(() => {
    if (availableCameras.value.length > 0 && availableCameras.value[currentCameraIndex.value]) {
        const currentCamera = availableCameras.value[currentCameraIndex.value];
        // 检查是否为前置摄像头
        if (isFrontCamera(currentCamera)) {
            const frontIndex = frontCameras.value.findIndex(cam => cam.deviceId === currentCamera.deviceId);
            if (frontIndex !== -1) {
                return `前置摄像头 ${frontIndex + 1}`;
            }
        }
        // 检查是否为后置摄像头
        else if (isBackCamera(currentCamera)) {
            const backIndex = backCameras.value.findIndex(cam => cam.deviceId === currentCamera.deviceId);
            if (backIndex !== -1) {
                return `后置摄像头 ${backIndex + 1}`;
            }
        }
        // 如果无法确定类型，则使用默认名称
        return currentCamera.label || `摄像头 ${currentCameraIndex.value + 1}`;
    }
    return '默认摄像头';
});

// 拍照
const capturePhoto = async () => {
    if (!videoRef.value) return
    
    const video = videoRef.value
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    
    const ctx = canvas.getContext('2d')
    if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        const originalBase64 = canvas.toDataURL('image/jpeg', 0.9)
        
        // 压缩图片
        try {
            const originalSize = getBase64Size(originalBase64)
            console.log(`原始图片大小: ${(originalSize / 1024).toFixed(1)}KB`)
            
            // 如果图片大于目标大小，进行压缩
            if (originalSize > compressConfig.maxSizeKB * 1024) {
                capturedImage.value = await compressImage(originalBase64)
                ElMessage.success('图片已自动压缩')
            } else {
                capturedImage.value = originalBase64
            }
        } catch (err) {
            console.error('图片压缩失败，使用原图:', err)
            capturedImage.value = originalBase64
        }
    }
}

// 重新拍照
const retakePhoto = () => {
    capturedImage.value = null
    // 重新初始化摄像头
    initCamera()
}

// 上传照片进行Base64识别
const uploadPhotoAsBase64 = async () => {
    if (!capturedImage.value) return
    
    uploading.value = true

    try {
        // 调用Base64识别API
        const response = await getDatas("home/GetDeviceReadingBase64", capturedImage.value.split(',')[1])
        
        // 添加到结果列表
        recognitionResults.value.unshift({
            id: Date.now(),
            image: capturedImage.value,
            result: response.response || response.data || '无识别结果',
            timestamp: new Date()
        })
        console.log();
        
        ElMessage.success('识别成功')
        capturedImage.value = null
        // 上传成功后重新初始化摄像头
        initCamera()
    } catch (err) {
        console.error('识别失败:', err)
        ElMessage.error('识别失败，请重试')
    } finally {
        uploading.value = false
    }
}

// 上传照片进行图片识别
const uploadPhotoAsImage = async () => {
    if (!capturedImage.value) return
    
    uploading.value = true
    
    try {
        // 将Base64转换为Blob对象
        const blob = await fetch(capturedImage.value).then(res => res.blob());
        const formData = new FormData();
        formData.append('image', blob, 'captured-image.jpg');
 
        // 调用图片识别API
        const response = await getDatas("home/GetDeviceReadingImage", formData);
        console.log(response);
        
        // 添加到结果列表
        recognitionResults.value.unshift({
            id: Date.now(),
            image: capturedImage.value,
            result: response.response || response.data || '无识别结果',
            timestamp: new Date()
        })
        
        ElMessage.success('识别成功')
        capturedImage.value = null
        // 上传成功后重新初始化摄像头
        initCamera()
    } catch (err) {
        console.error('识别失败:', err)
        ElMessage.error('识别失败，请重试')
    } finally {
        uploading.value = false
    }
}

// 根据选择的上传方法调用对应函数
const uploadPhoto = () => {
    if (uploadMethod.value === 'base64') {
        uploadPhotoAsBase64();
    } else {
        uploadPhotoAsImage();
    }
}

// 格式化时间
const formatTime = (date: Date) => {
    return date.toLocaleString('zh-CN')
}

// 清理资源
const cleanup = () => {
    if (mediaStream.value) {
        mediaStream.value.getTracks().forEach(track => track.stop())
        mediaStream.value = null
    }
}

// 重新尝试初始化摄像头
const retryInitCamera = () => {
    cleanup()
    initCamera()
}

// 组件挂载时初始化摄像头
onMounted(() => {
    initCamera()
})

// 组件卸载前清理资源
onBeforeUnmount(() => {
    cleanup()
})

// 页面关闭时也清理资源
onUnmounted(() => {
    cleanup()
})
</script>

<style lang="less" scoped>
.home {
    padding: 1rem;
    width: 100%;
    box-sizing: border-box; // 确保 padding 不会增加总宽度
    
    .camera-container {
        h2 {
            text-align: center;
            margin-bottom: 1rem;
        }
        
        .video-container {
            position: relative;
            width: 100%;
            max-width: 100%;
            margin: 0 auto;
            
            .video-stream {
                width: 100%;
                max-height: 70vh;
                object-fit: cover;
                background-color: #000;
                border-radius: 8px;
            }
            
            .camera-controls {
                position: absolute;
                bottom: 20px;
                left: 50%;
                transform: translateX(-50%);
                z-index: 10;
                display: flex;
                align-items: center;
                gap: 10px;
                
                .switch-camera-btn {
                    width: 40px;
                    height: 40px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
                }
                
                .capture-btn {
                    width: 60px;
                    height: 60px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
                }
            }
        }
        
        .preview-container {
            .captured-image {
                width: 100%;
                max-height: 70vh;
                object-fit: contain;
                border-radius: 8px;
                background-color: #f5f5f5;
            }
            
            .preview-controls {
                display: flex;
                justify-content: space-around;
                align-items: center;
                margin-top: 1rem;
                flex-wrap: wrap;
                gap: 10px;
                
                .el-select {
                    width: 150px;
                }
            }
        }
        
        .results-container {
            margin-top: 2rem;
            
            h3 {
                margin-bottom: 1rem;
            }
            
            .result-image {
                width: 80px;
                height: 80px;
                object-fit: cover;
                border-radius: 4px;
            }
            
            .no-result {
                color: #999;
                font-style: italic;
            }
        }
    }
}

// 移动端适配
@media (max-width: 768px) {
    .home {
        padding: 0.5rem;
        
        .camera-container {
            .video-container {
                .camera-controls {
                    .switch-camera-btn {
                        width: 35px;
                        height: 35px;
                    }
                    
                    .capture-btn {
                        width: 50px;
                        height: 50px;
                    }
                }
            }
            
            .preview-controls {
                flex-direction: column;
                align-items: center;
                
                .el-button, .el-select {
                    width: 100%;
                }
            }
        }
    }
}
</style>